/**
 * FdaLabelPdfVersionCompareLauncher — ONE "Compare versions" control.
 *
 * Previously this shipped two buttons with two different modals: a PDF↔PDF
 * page-image compare and a separate cross-format change log. That was the
 * mistake. There is one compare now, and the FORMAT of each side is just
 * another thing the user picks inside it — XML↔XML, PDF↔PDF, XML↔PDF,
 * PDF↔XML all open the same modal and read the same way.
 *
 * The option list is assembled here because only this component knows both
 * halves: the FDA PDF versions of this application, and the SPL revisions of
 * the drug it is linked to.
 */
import { useEffect, useMemo, useState } from 'react';
import { authApi } from '../../services/authApi';
import CrossFormatCompareModal, { type SideOption } from './CrossFormatCompareModal';

interface Ver {
  vstr: string;
  date: string;
  docid: string;
  /** The FDA version string, e.g. "125057s425lbl" — it names the submission,
   *  so it is shown instead of a synthesised ordinal. */
  label?: string;
}

interface SplRevision {
  /** `drugs.id` for the current revision, `drug_versions.id` for a
   *  historical one — the polymorphic identity the XML<->XML comparison
   *  takes. NOT the drug_id the cross-format endpoint wants, which is why
   *  both travel on the option rather than one being derived from the
   *  other. */
  id: number;
  source: 'fda_current' | 'fda_historical' | 'draft';
  version_number: number;
  effective_date: string | null;
  is_current: boolean;
}

export default function FdaLabelPdfVersionCompareLauncher({
  docId,
  brand,
  drugId,
}: {
  docId: number;
  brand: string;
  /** The SPL drug this document is linked to. Null when the resolver refused
   *  to link (several `drugs` rows share one typed FDA identity): the XML
   *  side simply is not offered, rather than erroring. */
  drugId?: number | null;
}) {
  const [pdfs, setPdfs] = useState<Ver[]>([]);
  const [revs, setRevs] = useState<SplRevision[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    authApi.getFdaLabelPdfVersions(docId)
      .then((r) => { if (alive) setPdfs(r.versions || []); })
      .catch(() => { /* compare is optional — stay quiet, not broken */ });
    return () => { alive = false; };
  }, [docId]);

  useEffect(() => {
    let alive = true;
    if (drugId == null) { setRevs([]); return; }
    authApi.getAllVersions(drugId)
      .then((r: { versions?: SplRevision[] }) => {
        if (alive) setRevs((r.versions ?? []).filter((v) => v.source !== 'draft'));
      })
      .catch(() => { if (alive) setRevs([]); });
    return () => { alive = false; };
  }, [drugId]);

  const options = useMemo<SideOption[]>(() => {
    const pdfOpts: SideOption[] = pdfs.map((v) => ({
      source: 'fda_pdf' as const,
      id: Number(v.docid),
      version: null,
      label: v.label || v.vstr,
      sublabel: v.date,
      // FDA action date: when this labelling action took effect.
      date: v.date || null,
      vstr: v.vstr,
    }));
    const xmlOpts: SideOption[] = drugId == null ? [] : revs.map((v) => ({
      source: 'spl_xml' as const,
      id: drugId,
      version: v.version_number,
      label: `rev ${v.version_number}${v.is_current ? ' (current)' : ''}`,
      sublabel: v.effective_date ? v.effective_date.slice(0, 10) : undefined,
      // SPL effective date. The modal sorts on this, so the two publication
      // streams interleave correctly instead of appearing as two blocks.
      date: v.effective_date,
      polySource: v.source,
      polyId: v.id,
    }));
    return [...pdfOpts, ...xmlOpts];
  }, [pdfs, revs, drugId]);

  // Nothing to compare against: one PDF and no linked SPL.
  if (options.length < 2) return null;

  // Defaults. The document being viewed is one side; its counterpart is the
  // SPL revision closest to it IN TIME when the drug is linked (the
  // cross-format case this family exists for), else the nearest other PDF.
  //
  // Then the two are ordered by DATE, not by which one the user happened to
  // open. Defaulting the viewed document to "newer" would invert the diff
  // whenever someone opens an older submission, turning every addition into
  // a removal.
  const when = (o: SideOption) => {
    const t = o.date ? Date.parse(o.date) : NaN;
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const thisPdf = options.find((o) => o.source === 'fda_pdf' && o.id === docId)
    ?? options[0]!;
  const nearest = (pool: SideOption[]) => pool.length
    ? pool.reduce((best, o) =>
        Math.abs(when(o) - when(thisPdf)) < Math.abs(when(best) - when(thisPdf))
          ? o : best)
    : undefined;
  const counterpart =
    nearest(options.filter((o) => o.source === 'spl_xml'))
    ?? nearest(options.filter((o) => o.source === 'fda_pdf' && o.id !== docId))
    ?? options.find((o) => o !== thisPdf)!;
  const [initialOld, initialNew] = when(counterpart) <= when(thisPdf)
    ? [counterpart, thisPdf]
    : [thisPdf, counterpart];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button onClick={() => setOpen(true)} className="btn-secondary">
        Compare versions
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3"
          onClick={() => setOpen(false)}
        >
          <div
            className="h-full max-h-[93vh] w-full max-w-[1700px] overflow-hidden rounded-lg bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <CrossFormatCompareModal
              brand={brand}
              options={options}
              initialOld={initialOld}
              initialNew={initialNew}
              onClose={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
